import type { RequestContext } from "../../../application/context/request-context.js";
import type { MeReport } from "../../../domain/engineering/me-report-domain.js";
import type { MeReportRepository } from "../../../repositories/operational-repositories.js";

const STORAGE_KEY = "me_reports";

export class InMemoryMeReportRepository implements MeReportRepository {
  private reports = new Map<string, MeReport>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: MeReport[] = JSON.parse(stored);
          for (const r of parsed) {
            this.reports.set(r.id, r);
          }
        }
      }
    } catch (e) {
      console.warn("Failed to load ME reports from storage:", e);
    }
  }

  private persistStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.reports.values())));
      }
    } catch (e) {
      console.warn("Failed to persist ME reports to storage:", e);
    }
  }

  async findById(context: RequestContext, id: string): Promise<MeReport | null> {
    this.loadFromStorage();
    return this.reports.get(id) || null;
  }

  async findByInspection(context: RequestContext, inspectionId: string): Promise<MeReport | null> {
    this.loadFromStorage();
    for (const report of this.reports.values()) {
      if (report.inspectionId === inspectionId) return report;
    }
    return null;
  }

  async listByClient(context: RequestContext, clientId: string): Promise<readonly MeReport[]> {
    this.loadFromStorage();
    return Array.from(this.reports.values()).filter(r => r.clientId === clientId);
  }

  async listAll(): Promise<readonly MeReport[]> {
    this.loadFromStorage();
    return Array.from(this.reports.values());
  }

  async save(context: RequestContext, report: MeReport): Promise<void> {
    this.reports.set(report.id, { ...report });
    this.persistStorage();
  }
}
